import moment from 'moment';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalizedCalendarDay } from '@treams/treams-hooks';
import {
	ChevronDownIcon,
	FriendsIcon,
	LockIcon,
	MailIcon,
	MicrosoftOutlookIcon,
	RepeatIcon,
	ReplyIcon,
} from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Checkbox,
	FlexGap,
	Heading3,
	IconWrapper,
	InlineIconWrapper,
	Join,
	Label,
	Paragraph,
	PlainCard,
	Skeleton,
	tokens,
} from '@treams/treams-ui';

import { useModalLink } from '../navigation/ModalNavigation';
import microsoftTeamsIcon from './assets/microsoft-teams-icon.svg?url';
import { buildNoteListSummaryPreview } from './quillPlainTextPreview';

const SnippetBox = styled(Box)`
	background: ${tokens.surface.secondary};
	border: 1px solid ${tokens.border.subtle};
	border-radius: ${tokens.radius.sm}px;
	padding: ${tokens.padding.control}px;
`;

const SourceIcon = styled.div`
	width: 40px;
	height: 40px;
	border-radius: ${tokens.radius.sm}px;
	background: ${tokens.brand.background};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;

	svg {
		display: block;
		width: 20px;
		height: 20px;
		color: ${tokens.brand.icon};

		path {
			fill: ${tokens.brand.icon};
		}
	}

	img {
		display: block;
		width: 20px;
		height: 20px;
	}
`;

const ShowMoreHint = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${tokens.gap.inline}px;
	margin-top: ${tokens.gap.element}px;
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: ${tokens.typography.weights.semibold};
	color: ${tokens.brand.text};

	svg {
		width: 14px;
		height: 14px;
		path {
			fill: ${tokens.brand.icon};
		}
	}
`;

const OUTLOOK_BRAND_BLUE = 'rgba(0, 114, 198, 1)';

export const OutlookBadge = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${tokens.gap.element}px;

	svg {
		width: 16px;
		height: 16px;
		display: block;
		fill: ${OUTLOOK_BRAND_BLUE};
	}

	svg path {
		fill: ${OUTLOOK_BRAND_BLUE};
	}
`;

const TeamsBadge = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${tokens.gap.element}px;

	img {
		width: 16px;
		height: 16px;
		display: block;
	}
`;

const MutedInlineIcon = styled.span`
	display: inline-flex;
	line-height: 0;
	vertical-align: middle;
	transform: scaleX(-1);

	svg {
		width: 16px;
		height: 16px;
		display: block;
		color: ${tokens.icon.secondary};
	}

	svg path {
		fill: ${tokens.icon.secondary};
	}
`;

function noteHeadingTitle(
	note: graphql.NoteForOneOnOneOverviewFragment,
	t: (key: string, opts?: Record<string, unknown>) => string,
): string {
	if (note.title) {
		return note.title;
	}
	if (note.subject) {
		return t('oneOnOne.index.card.titleWithSubject', { subject: note.subject.firstName });
	}
	return t('note.index.card.title');
}

function stopCardNavigation(event: { preventDefault(): void; stopPropagation(): void }) {
	event.preventDefault();
	event.stopPropagation();
}

export default Skeleton(
	function RichNoteCard(props: {
		note: graphql.NoteForOneOnOneOverviewFragment;
		userID: string;
		linkPrefix?: string;
		modal?: boolean;
	}) {
		const { t } = useTranslation();
		const link = useModalLink(
			`${props.linkPrefix ?? ''}/notes/${props.note.id}`,
			props.modal ?? false,
		);

		const calendarDayDate = useLocalizedCalendarDay(props.note.meeting?.startsAt, {
			includeTime: true,
			capitalize: true,
		});

		const heading = noteHeadingTitle(props.note, t);

		const visibility = useMemo(() => {
			const edges = props.note.collaborators.edges;
			const hasNextPage = props.note.collaborators.pageInfo?.hasNextPage ?? true;

			if (edges.length === 1 && edges[0].node.id === props.userID && !hasNextPage) {
				return t('oneOnOne.richCard.onlyYou');
			}
			if (edges.length === 0 && props.note.noteType === graphql.NoteType.Conversation) {
				return props.note.owner.id === props.userID
					? t('oneOnOne.richCard.onlyYou')
					: props.note.createdBy.firstName;
			}
			const collabs = edges.map((e) => e.node);
			const others = collabs.filter((u) => u.id !== props.userID);
			const isViewerCollaborator = others.length < collabs.length;

			if (isViewerCollaborator && others.length === 1) {
				return t('oneOnOne.richCard.youAnd', { name: others[0].firstName });
			}
			return collabs.map((u) => u.firstName).join(', ');
		}, [
			props.note.collaborators,
			props.note.noteType,
			props.note.owner.id,
			props.note.createdBy.firstName,
			props.userID,
			t,
		]);

		const visibilityIcon =
			props.note.collaborators.edges.length <= 1 ? (
				<LockIcon aria-hidden />
			) : (
				<FriendsIcon aria-hidden />
			);

		const summaryPreview = useMemo(() => buildNoteListSummaryPreview(props.note), [props.note]);

		const [updateTask] = graphql.useUpdateTaskMutation();

		const noteTasks = props.note.tasks ?? [];
		const canManageTasks = props.userID === props.note.owner?.id;

		const handleToggleTask = useCallback(
			async (task: graphql.NoteTaskFragment) => {
				const next =
					task.status === graphql.TaskStatus.Done
						? graphql.TaskStatus.Todo
						: graphql.TaskStatus.Done;
				await updateTask({
					variables: { input: { id: task.id, status: next } },
					refetchQueries: 'active',
					awaitRefetchQueries: true,
				});
			},
			[updateTask],
		);

		const meta = (
			<Join separator=" · ">
				{calendarDayDate && (
					<>
						{calendarDayDate}
						{props.note.meeting?.series && (
							<>
								&nbsp;
								<InlineIconWrapper size={16} translateY={-2}>
									<RepeatIcon />
								</InlineIconWrapper>
							</>
						)}
					</>
				)}
				{t('oneOnOne.index.card.updated', {
					date: moment(props.note.updatedAt).fromNow(),
				})}
			</Join>
		);

		const actionPointsSection =
			noteTasks.length > 0 ? (
				<Box>
					<Label mb={`${tokens.gap.element}px`}>{t('oneOnOne.richCard.actionPoints')}</Label>
					<FlexGap flexDirection="column" gap={tokens.gap.element}>
						{noteTasks.map((item) => (
							<FlexGap key={item.id} alignItems="center" gap={tokens.gap.element}>
								<Box onClick={stopCardNavigation} onKeyDown={stopCardNavigation}>
									<Checkbox
										mr={0}
										checked={item.status === graphql.TaskStatus.Done}
										disabled={!canManageTasks}
										onClick={() => {
											if (canManageTasks) {
												void handleToggleTask(item);
											}
										}}
										aria-label={
											item.status === graphql.TaskStatus.Done
												? t('oneOnOne.tasks.markTodo')
												: t('oneOnOne.tasks.markDone')
										}
									/>
								</Box>
								<Paragraph mb={0}>{item.title}</Paragraph>
							</FlexGap>
						))}
					</FlexGap>
				</Box>
			) : null;

		const isOutlook = props.note.metadata?.source === 'OUTLOOK';
		const isTeams = props.note.metadata?.source === 'MSTEAMS';

		const header = (
			<Box>
				<Heading3>{heading}</Heading3>
				<Paragraph mt="4px">{meta}</Paragraph>
			</Box>
		);

		const visibilityRow = (
			<FlexGap alignItems="center" gap={tokens.gap.element}>
				<Avatar.Stack users={props.note.collaborators.edges.map((e) => e.node)} />
				<FlexGap alignItems="center" gap={tokens.gap.inline}>
					<IconWrapper size={16} color={tokens.icon.secondary}>
						{visibilityIcon}
					</IconWrapper>
					<Paragraph mb={0} small>
						{visibility}
					</Paragraph>
				</FlexGap>
			</FlexGap>
		);

		if (isOutlook) {
			return (
				<PlainCard to={link}>
					<Flex flexDirection="column" style={{ gap: tokens.gap.list }}>
						{header}
						{visibilityRow}
						<FlexGap gap={tokens.gap.list} alignItems="flex-start">
							<SourceIcon>
								<MailIcon aria-hidden />
							</SourceIcon>
							<Box flex={1} minWidth={0}>
								{props.note.subject && (
									<Paragraph mb={`${tokens.gap.element}px`}>
										{t('oneOnOne.richCard.aboutSubject', {
											name: props.note.subject.firstName,
										})}
									</Paragraph>
								)}
								{summaryPreview ? (
									<SnippetBox>
										<Paragraph mb={0} style={{ fontStyle: 'italic' }}>
											{summaryPreview}
										</Paragraph>
										<ShowMoreHint>
											{t('oneOnOne.richCard.showFullEmail')}
											<ChevronDownIcon aria-hidden />
										</ShowMoreHint>
									</SnippetBox>
								) : null}
							</Box>
						</FlexGap>
						{actionPointsSection}
						<FlexGap
							alignItems="center"
							justifyContent="space-between"
							flexWrap="wrap"
							gap={tokens.gap.element}
						>
							<FlexGap alignItems="center" gap={tokens.gap.element}>
								<MutedInlineIcon>
									<ReplyIcon aria-hidden />
								</MutedInlineIcon>
								<Paragraph mb={0} small>
									{props.note.metadata?.sender
										? t('oneOnOne.richCard.forwardedBy', {
												sender: props.note.metadata.sender,
											})
										: t('oneOnOne.richCard.forwardedByUnknown')}
								</Paragraph>
							</FlexGap>
							<OutlookBadge>
								<MicrosoftOutlookIcon aria-hidden />
								<Paragraph mb={0} small>
									{t('oneOnOne.richCard.outlookIntegration')}
								</Paragraph>
							</OutlookBadge>
						</FlexGap>
					</Flex>
				</PlainCard>
			);
		}

		if (isTeams) {
			return (
				<PlainCard to={link}>
					<Flex flexDirection="column" style={{ gap: tokens.gap.list }}>
						{header}
						{visibilityRow}
						<FlexGap gap={tokens.gap.list} alignItems="flex-start">
							<SourceIcon>
								<img src={microsoftTeamsIcon} alt="" width={20} height={20} />
							</SourceIcon>
							<Box flex={1} minWidth={0}>
								{props.note.subject && (
									<Paragraph mb={`${tokens.gap.element}px`}>
										{t('oneOnOne.richCard.aboutSubject', {
											name: props.note.subject.firstName,
										})}
									</Paragraph>
								)}
								{summaryPreview ? (
									<SnippetBox>
										<Paragraph mb={0} style={{ fontStyle: 'italic' }}>
											{summaryPreview}
										</Paragraph>
										<ShowMoreHint>
											{t('oneOnOne.richCard.showFullTeamsMessage')}
											<ChevronDownIcon aria-hidden />
										</ShowMoreHint>
									</SnippetBox>
								) : null}
							</Box>
						</FlexGap>
						{actionPointsSection}
						<FlexGap
							alignItems="center"
							justifyContent="space-between"
							flexWrap="wrap"
							gap={tokens.gap.element}
						>
							<FlexGap alignItems="center" gap={tokens.gap.element}>
								<MutedInlineIcon>
									<ReplyIcon aria-hidden />
								</MutedInlineIcon>
								<Paragraph mb={0} small>
									{props.note.metadata?.sender
										? t('oneOnOne.richCard.teamsMessageFrom', {
												sender: props.note.metadata.sender,
											})
										: t('oneOnOne.richCard.teamsMessageUnknown')}
								</Paragraph>
							</FlexGap>
							<TeamsBadge>
								<img src={microsoftTeamsIcon} alt="" width={16} height={16} />
								<Paragraph mb={0} small>
									{t('microsoft.teams.app.name')}
								</Paragraph>
							</TeamsBadge>
						</FlexGap>
					</Flex>
				</PlainCard>
			);
		}

		return (
			<PlainCard to={link}>
				<Flex flexDirection="column" style={{ gap: tokens.gap.list }}>
					{header}
					{visibilityRow}
					{summaryPreview ? <Paragraph mb={0}>{summaryPreview}</Paragraph> : null}
					{actionPointsSection}
				</Flex>
			</PlainCard>
		);
	},
	function RichNoteCardSkeleton() {
		return (
			<PlainCard.Skeleton>
				<Flex flexDirection="column" style={{ gap: tokens.gap.list }}>
					<Box>
						<Heading3.Skeleton />
						<Paragraph.Skeleton mt="4px" lines={1} />
					</Box>
					<FlexGap alignItems="center" gap={tokens.gap.element}>
						<Avatar.Skeleton size={32} />
						<Avatar.Skeleton size={32} />
						<Paragraph.Skeleton mt="0" lines={1} />
					</FlexGap>
				</Flex>
			</PlainCard.Skeleton>
		);
	},
);
